import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSwipeToClose from "../../hooks/useSwipeToClose";
import { useStripePrices } from "../../services/priceConfig";
import { getSafeStripeReturnPath, redirectToCheckout } from "../../services/stripeService";
import backendClient from "../../services/backendClient";
import { getFingerprint } from "../../utils/fingerprint";
import LoginModal from "../LoginModal/LoginModal";
import { useUser } from "../../contexts/UserContext";
import {
  trackLimitReached,
  trackPaywallViewed,
  LIMIT_TYPE,
  PAYWALL_TRIGGER,
  PAYWALL_TYPE,
  CONTEXT_ACTION,
} from "../../analytics";

function TriesLeftDrawer({
  isOpen,
  onClose,
  onTryAgain,
  triesLeft: initialTriesLeft = 2,
  isRegistered = false,
  onTriesUpdate,
}) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();
  const { savePreferences } = useUser();
  const [triesLeft, setTriesLeft] = useState(initialTriesLeft);
  const [isLoadingTries, setIsLoadingTries] = useState(false);
  const [isYearly, setIsYearly] = useState(true); // Payment mode toggle state
  const prices = useStripePrices();
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [hasUsedTrial, setHasUsedTrial] = useState(false);
  const trialDays = prices.trialDays ?? 5;
  // Non-logged-in users are assumed trial-eligible; logged-in users depend on the flag
  const isLoggedIn = !!localStorage.getItem('authToken');
  const trialEligible = !isLoggedIn || !hasUsedTrial;
  const afterTrialCopy = isYearly
    ? t("premium.afterTrialAnnual", { price: prices.yearlyPerMonth, total: prices.yearlyTotal })
    : t("premium.afterTrialMonthly", { price: prices.monthlyPerMonth });
  const premiumFeatures = [
    t("premium.feature1"),
    t("premium.feature2"),
    t("premium.feature3"),
    t("premium.feature4"),
    t("premium.feature5"),
  ];

  const proceedToCheckout = async () => {
    setIsCheckoutLoading(true);
    try {
      await redirectToCheckout({
        isYearly,
        returnPath: getSafeStripeReturnPath(`${location.pathname}${location.search || ''}`),
      });
    } catch (err) {
      console.error('[TriesLeftDrawer] Checkout error:', err);
      setIsCheckoutLoading(false);
    }
  };

  const handleStartPremium = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      onClose();
      // Small delay to let drawer close animation finish before showing login modal
      setTimeout(() => setIsLoginModalOpen(true), 150);
      return;
    }
    await proceedToCheckout();
  };

  const handleLoginSuccess = async () => {
    setIsLoginModalOpen(false);
    // Sync localStorage preferences to backend now that user is authenticated
    try {
      await savePreferences();
    } catch (err) {
      console.error('[TriesLeftDrawer] Error saving preferences after login:', err);
    }
    try {
      const userData = await backendClient.get('/api/user/me');
      if (userData?.isPremium) {
        navigate('/wizard');
        return;
      }
      if (userData?.hasUsedFreeTrial) setHasUsedTrial(true);
    } catch (err) {
      console.error('[TriesLeftDrawer] Error checking premium:', err);
    }
    await proceedToCheckout();
  };

  // Fetch fresh tries left data when drawer opens
  useEffect(() => {
    const fetchTriesLeft = async () => {
      if (!isOpen) return;

      setIsLoadingTries(true);
      try {
        // Fetch trial eligibility for logged-in users
        const authToken = localStorage.getItem("authToken");
        if (authToken) {
          backendClient.get('/api/user/me').then((userData) => {
            if (userData?.hasUsedFreeTrial) setHasUsedTrial(true);
          }).catch(() => {});
        }

        const params = new URLSearchParams();
        if (!authToken) {
          const fp = getFingerprint();
          if (fp) params.set("fingerprint", fp);
        }
        const qs = params.toString();
        const data = await backendClient.get(`/api/meal-plan-limit/check${qs ? `?${qs}` : ""}`);
        console.log("[TriesLeftDrawer] Fetched data:", data);
        // Backend sends triesRemaining (can be number or 'unlimited' for premium)
        if (data.triesRemaining !== undefined) {
          // For premium users, triesRemaining is 'unlimited' - treat as infinite
          if (data.triesRemaining === 'unlimited' || data.isPremium) {
            setTriesLeft(Infinity);
          } else {
            setTriesLeft(data.triesRemaining);
            // Sync the freshly-fetched count back to the parent so other
            // entry points (logo / back arrow / sticky footer) see an
            // up-to-date value.
            if (typeof onTriesUpdate === 'function') {
              onTriesUpdate(data.triesRemaining);
            }
          }
        }
      } catch (error) {
        console.error("[TriesLeftDrawer] Error fetching tries left:", error);
        // Use initial prop value as fallback
        setTriesLeft(initialTriesLeft);
      } finally {
        setIsLoadingTries(false);
      }
    };

    fetchTriesLeft();
    // onTriesUpdate is intentionally omitted — parent passes a stable
    // setState ref; including it would cause a re-fetch every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialTriesLeft]);

  const {
    containerRef,
    dragOffset,
    shouldRender,
    isVisible,
    DragHandle,
  } = useSwipeToClose(onClose, isOpen);

  // Track when drawer opens
  const hasTracked = useRef(false);
  useEffect(() => {
    if (isOpen && !hasTracked.current) {
      hasTracked.current = true;

      // Track Limit Reached
      trackLimitReached({
        limit_type: LIMIT_TYPE.GENERATIONS,
        current_count: triesLeft,
        max_count: 3, // Default max tries for free users
      });

      // If no tries left, this is effectively a paywall
      if (triesLeft === 0) {
        trackPaywallViewed({
          trigger: PAYWALL_TRIGGER.GENERATION_LIMIT,
          paywall_type: PAYWALL_TYPE.HARD,
          context_action: CONTEXT_ACTION.GENERATE,
          tries_remaining: 0,
        });
      }
    }
  }, [isOpen, triesLeft]);

  if (!shouldRender && !isLoginModalOpen) return null;

  const portalContent = shouldRender ? createPortal(
    <div
      className={`fixed flex items-end justify-center inset-0 bg-[#0D0C07] z-[200] drawer-overlay`}
      style={{
        backgroundColor: `rgba(13, 12, 7, ${isVisible ? 0.5 : 0})`,
        transition:
          "background-color 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)",
      }}
      onClick={onClose}
    >
      <div
        ref={containerRef}
        className={`flex flex-col w-full bg-[#F8F8F7] rounded-[12px_12px_0px_0px] max-w-5xl mx-auto shadow-lg drawer-content max-h-[90vh]`}
        style={{
          transform: isVisible
            ? `translateY(${Math.max(0, dragOffset)}px)`
            : "translateY(100%)",
          transition:
            dragOffset === 0
              ? "transform 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)"
              : "none",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag Handle */}
        <DragHandle />
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-1 right-1 flex justify-center items-center md:hover:bg-gray-200 w-[36px] h-[36px] rounded-[12px] transition-colors z-20"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M16.381 6.38079C16.7227 6.03908 17.2776 6.03908 17.6193 6.38079C17.961 6.7225 17.961 7.27737 17.6193 7.61907L13.2385 11.9999L17.6193 16.3808L17.6789 16.4472C17.9595 16.7909 17.9398 17.2986 17.6193 17.6191C17.2989 17.9395 16.7911 17.9592 16.4474 17.6786L16.381 17.6191L12.0002 13.2382L7.61932 17.6191C7.27761 17.9608 6.72275 17.9608 6.38104 17.6191C6.03933 17.2774 6.03933 16.7225 6.38104 16.3808L10.7619 11.9999L6.38104 7.61907L6.32147 7.55267C6.04089 7.20899 6.06059 6.70124 6.38104 6.38079C6.70149 6.06034 7.20923 6.04064 7.55291 6.32122L7.61932 6.38079L12.0002 10.7617L16.381 6.38079Z"
              fill="#56554D"
            />
          </svg>
        </button>

        {/* Scrollable content */}
        <div
          className={`flex-1 overflow-y-auto min-h-0 overflow-x-hidden p-8 px-6 ${
            !isLoadingTries && triesLeft > 0 ? "pb-[108px]" : "pb-8"
          }`}
        >
          <div className="pt-2 w-full">
            {isLoadingTries ? (
              /* ===== LOADING STATE — prevents flicker between branches ===== */
              <div className="flex items-center justify-center py-16 min-h-[320px]">
                <svg className="animate-spin h-8 w-8 text-[#56554D]" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
              </div>
            ) : triesLeft > 0 ? (
              /* ===== HAS TRIES LAYOUT ===== */
              <>
                {/* Title Section */}
                <div className="pb-3">
                  <h2 className="text-[#0d0c07] text-[24px] font-bold leading-[40px]">
                    {t("premium.createNewMeals")}
                  </h2>
                </div>{" "}
                {/* Cards Container */}
                <div className="flex flex-col gap-5">
                  {/* Info Card - Yellow */}
                  <div className="bg-[#ffcb00] relative rounded-[12px]">
                    <div className="flex gap-3 items-start px-4 py-3">
                      {/* Icon */}
                      <div className="flex items-center justify-center h-7 py-1 shrink-0">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M8.3335 1.66667H11.6668M10.0002 11.6667V8.33334M3.3335 10.8333C3.53755 9.21332 4.32904 7.72451 5.55787 6.64929C6.78669 5.57407 8.36739 4.9872 10.0002 5.00001C11.0785 5.00115 12.1405 5.26387 13.0951 5.76564C14.0496 6.2674 14.8681 6.99323 15.4805 7.88086C16.0929 8.76848 16.4808 9.79142 16.611 10.8619C16.7412 11.9324 16.6097 13.0185 16.2279 14.027C15.8461 15.0355 15.2254 15.9364 14.4189 16.6523C13.6125 17.3682 12.6444 17.8778 11.5977 18.1373C10.551 18.3969 9.45703 18.3987 8.40952 18.1425C7.36201 17.8864 6.39228 17.3799 5.5835 16.6667L3.3335 14.6667M7.50016 14.1667H3.3335V18.3333"
                            stroke="#0D0C07"
                            strokeWidth="1.75"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      {/* Text */}
                      <p
                        className="text-[#0d0c07] text-[15.2px] leading-[28px]"
                        dangerouslySetInnerHTML={{
                          __html: isRegistered
                            ? t("premium.triesAvailableRegistered", {
                                count: triesLeft,
                                planWord:
                                  triesLeft === 1
                                    ? t("premium.mealPlan")
                                    : t("premium.mealPlans"),
                              })
                            : t("premium.triesAvailable", {
                                count: triesLeft,
                                planWord:
                                  triesLeft === 1
                                    ? t("premium.mealPlan")
                                    : t("premium.mealPlans"),
                              }),
                        }}
                      />
                    </div>
                  </div>

                  {/* Premium Card - Gray */}
                  <div className="bg-[#f3f3f2] relative rounded-[12px] border-[0.5px] border-[#e2e1df]">
                    <div className="flex gap-3 items-start px-4 pt-4 pb-5">
                      {/* Icon */}
                      <div className="flex items-center justify-center h-7 py-1 shrink-0">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <g clip-path="url(#clip0_2399_16111)">
                            <path
                              d="M16.6667 2.5V5.83333M18.3334 4.16667H15M3.33336 14.1667V15.8333M4.1667 15H2.50003M8.28086 12.9167C8.20647 12.6283 8.05615 12.3651 7.84555 12.1545C7.63494 11.9439 7.37176 11.7936 7.08336 11.7192L1.97086 10.4008C1.88364 10.3761 1.80687 10.3235 1.75221 10.2512C1.69754 10.1789 1.66797 10.0907 1.66797 10C1.66797 9.90933 1.69754 9.82113 1.75221 9.7488C1.80687 9.67646 1.88364 9.62392 1.97086 9.59917L7.08336 8.28C7.37166 8.20567 7.63477 8.05548 7.84537 7.84503C8.05596 7.63459 8.20634 7.37157 8.28086 7.08333L9.5992 1.97083C9.6237 1.88326 9.67618 1.80612 9.74863 1.75116C9.82108 1.69621 9.90951 1.66646 10.0004 1.66646C10.0914 1.66646 10.1798 1.69621 10.2523 1.75116C10.3247 1.80612 10.3772 1.88326 10.4017 1.97083L11.7192 7.08333C11.7936 7.37173 11.9439 7.63491 12.1545 7.84551C12.3651 8.05612 12.6283 8.20643 12.9167 8.28083L18.0292 9.59833C18.1171 9.62258 18.1946 9.67501 18.2499 9.74756C18.3052 9.82012 18.3351 9.9088 18.3351 10C18.3351 10.0912 18.3052 10.1799 18.2499 10.2524C18.1946 10.325 18.1171 10.3774 18.0292 10.4017L12.9167 11.7192C12.6283 11.7936 12.3651 11.9439 12.1545 12.1545C11.9439 12.3651 11.7936 12.6283 11.7192 12.9167L10.4009 18.0292C10.3764 18.1167 10.3239 18.1939 10.2514 18.2488C10.179 18.3038 10.0905 18.3335 9.99961 18.3335C9.90868 18.3335 9.82025 18.3038 9.7478 18.2488C9.67535 18.1939 9.62287 18.1167 9.59836 18.0292L8.28086 12.9167Z"
                              stroke="#0D0C07"
                              stroke-width="1.75"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_2399_16111">
                              <rect width="20" height="20" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                      </div>
                      {/* Content */}
                      <div className="flex flex-col flex-grow">
                        {/* Info */}
                        <div className="flex flex-col gap-1">
                          <p className="text-[#0d0c07] text-[15.2px] leading-[28px] font-bold">
                            {trialEligible
                              ? t("premium.trialBadge", { days: trialDays })
                              : t("premium.title")}
                          </p>
                          <p className="text-[#006600] text-[14px] leading-[24px]">
                            {trialEligible ? afterTrialCopy : t("premium.becomePremium")}
                          </p>
                        </div>
                        {/* Link */}
                        <Link
                          to="/premium"
                          className="text-[#0d0c07] font-semibold md:hover:border-[#0d0c07] text-[14px] leading-[24px] border-b border-[#ffcb00] inline-block self-start"
                          onClick={onClose}
                        >
                          {t("premium.discoverFeatures")}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              /* ===== NO TRIES LEFT LAYOUT ===== */
              <>
                {/* Title Section */}
                <div className="pb-6 text-center">
                  <div className="flex justify-center mb-4">
                    <div className="inline-flex items-center gap-2 rounded-full bg-[#FFCB00] px-4 py-2 text-[#0D0C07] text-[13px] font-semibold leading-[18px]">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M13 2L14.8 8.2L21 10L14.8 11.8L13 18L11.2 11.8L5 10L11.2 8.2L13 2Z"
                          stroke="#0D0C07"
                          strokeWidth="1.75"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5 15L5.9 18.1L9 19L5.9 19.9L5 23L4.1 19.9L1 19L4.1 18.1L5 15Z"
                          stroke="#0D0C07"
                          strokeWidth="1.75"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {trialEligible
                        ? t("premium.trialBadge", { days: trialDays })
                        : t("premium.title")}
                    </div>
                  </div>
                  <h2 className="text-[#0d0c07] text-center text-[22px] font-bold leading-[36px]">
                    {t("premium.subTitleTop", { days: trialDays })}
                  </h2>
                  <p className="text-[#56554D] text-center text-[15.2px] font-normal leading-[28px] mt-1">
                    {trialEligible
                      ? t("premium.subTitleMain", { days: trialDays })
                      : t("premium.becomePremium")}
                  </p>
                </div>
                {/* Yearly/Monthly Toggle */}
                <div className="relative flex justify-center items-center pb-6">
                  <div className="relative flex w-full max-w-md bg-[#E5E5EA] rounded-[32px] p-1">
                    {/* Sliding background indicator */}
                    <div
                      className={`
                        absolute top-1 bottom-1 left-1 w-[calc(50%-4px)] bg-white rounded-[28px]
                        transition-transform duration-300 ease-in-out shadow-md
                        ${isYearly ? "translate-x-full" : "translate-x-0"}
                      `}
                    />
                    {/* Monthly button */}
                    <button
                      onClick={() => setIsYearly(false)}
                      className={`
                        relative z-10 flex-1 min-w-0 py-2.5 px-2 text-[13.2px] font-semibold rounded-[28px]
                        transition-colors duration-300 ease-in-out text-center
                        ${!isYearly ? "text-[#0D0C07]" : "text-[#56554D]"}
                      `}
                    >
                      {t("premium.monthly")}
                    </button>
                    {/* Annual button */}
                    <button
                      onClick={() => setIsYearly(true)}
                      className={`
                        relative z-10 flex-1 min-w-0 py-2.5 px-2 text-[13.2px] font-semibold rounded-[28px]
                        transition-colors duration-300 ease-in-out flex items-center justify-center gap-1.5
                        ${isYearly ? "text-[#0D0C07]" : "text-[#56554D]"}
                      `}
                    >
                      <span className="truncate">{t("premium.annual")}</span>
                      <span className="text-[10px] leading-[14px] font-semibold text-[#0D0C07] bg-[#FFCB00] px-1.5 py-0.5 rounded-full whitespace-nowrap">
                        {t("premium.save10")}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Card Container */}
                <div className="bg-white relative rounded-[12px] md:p-8 p-5 shadow-[0px_1px_2px_0px_rgba(35,33,22,0.02),4px_12px_20px_0px_rgba(83,81,69,0.12)] border-[0.5px] border-[#e2e1df] max-w-lg mx-auto">

                  {/* Features List */}
                  <div className="flex flex-col">
                    <div className="rounded-[10px] bg-[#F8F8F7] border border-[#E8E7E3] p-5 mb-6">
                      {trialEligible ? (
                        <>
                          <p className="text-[#518C03] text-[13px] leading-[20px] font-semibold mb-2">
                            {t("premium.trialEyebrow", { days: trialDays })}
                          </p>
                          <div className="flex items-end gap-2 mb-2">
                            <span className="text-[#0D0C07] text-[46px] leading-[52px] font-bold tracking-[0]">
                              {t("premium.trialPrice")}
                            </span>
                            <span className="text-[#56554D] text-[14px] leading-[22px] pb-2">
                              {t("premium.trialPriceSuffix", { days: trialDays })}
                            </span>
                          </div>
                          <p className="text-[#56554D] text-[14px] leading-[24px]">
                            {afterTrialCopy}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-[#518C03] text-[24px] leading-[32px] font-light mb-1">
                            {isYearly
                              ? t("premium.onlyPrice", { price: prices.yearlyPerMonth })
                              : t("premium.monthlyPrice", { price: prices.monthlyPerMonth })}
                          </p>
                          <p className={`text-[15.2px] font-normal leading-[28px] text-[#0D0C07] ${isYearly ? '' : 'invisible'}`}>
                            {t("premium.yearPriceTotal", { price: prices.yearlyTotal })}
                          </p>
                        </>
                      )}
                    </div>

                    {/* Become Premium Button */}
                  <div className="mb-4">
                    <button
                      onClick={handleStartPremium}
                      disabled={isCheckoutLoading}
                      className="
                        w-full
                        inline-flex justify-center items-center gap-1
                        h-[48px] px-[24px] py-[14px]
                        rounded-[32px]
                        border-[1px] border-[linear-gradient(200deg,_#090_0%,_#060_100%)]
                        bg-[#090]
                        text-[#FFF] text-center
                        text-[14.2px] font-semibold leading-[28px]
                        transition-colors
                        md:hover:border-[1px] md:hover:border-[#060]
                        md:hover:bg-[#060]
                        bg-[linear-gradient(200deg,_#090_0%,_#060_100%)]
                        shadow-[0_2px_16px_0_rgba(0,0,0,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_2px_rgba(0,102,0,0.4)]
                        disabled:opacity-70 cursor-pointer
                      "
                    >
                      {isCheckoutLoading ? (
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                        </svg>
                      ) : (
                        <>
                          <span>{trialEligible
                            ? t("premium.becomePremiumButton", { days: trialDays })
                            : t("premium.subscribeButton")}</span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M11.3816 5.54742C11.702 5.22734 12.2089 5.20753 12.5525 5.48785L12.6189 5.54742L18.4519 11.3814C18.7936 11.7231 18.7936 12.277 18.4519 12.6187L12.6189 18.4517L12.5525 18.5123C12.2088 18.7925 11.7019 18.7721 11.3816 18.4517C11.0399 18.11 11.0399 17.5561 11.3816 17.2144L15.7214 12.8746H6.16675C5.6835 12.8746 5.29175 12.4828 5.29175 11.9996C5.29192 11.5165 5.68361 11.1246 6.16675 11.1246H15.7205L11.3816 6.7857L11.321 6.7193C11.0406 6.37562 11.0612 5.86782 11.3816 5.54742Z"
                              fill="currentColor"
                            />
                          </svg>
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-[#56554D] text-[13.2px] leading-[20px] mb-6 text-center">
                    {trialEligible
                      ? t("premium.cancelAnytime", { days: trialDays })
                      : t("premium.cancelAnytimeSimple")}
                  </p>
                  <div className="h-px bg-[#E8E7E3] mb-6" />
                    
                    <div className="flex flex-col gap-4">
                      {premiumFeatures.map((feature) => (
                        <div key={feature} className="flex gap-3 items-start">
                          <div className="bg-[#f3f3f2] flex items-center justify-center rounded-[6px] size-[28px] shrink-0">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              aria-hidden="true"
                            >
                              <path
                                d="M20 6L9 17L4 12"
                                stroke="#0D0C07"
                                strokeWidth="1.75"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <span className="text-[#0d0c07] text-[14.2px] leading-[24px]">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Fixed Sticky Area - Only show when user has tries */}
        {!isLoadingTries && triesLeft > 0 && (
          <div
            className="
              fixed bottom-[16px] left-[24px] right-[24px]
              py-2 px-2
              flex justify-center
              bg-[linear-gradient(145deg,_rgba(255,255,255,0.9)_0%,_rgba(255,255,255,0.6)_100%)]
              backdrop-blur-lg
              z-50
              mx-auto
              rounded-[96px]
              border-[0.5px] border-[linear-gradient(145deg,_rgba(255,255,255,0.7)_0%,_rgba(255,255,255,1)_100%)]
              shadow-[0px_0px_4px_0px_rgba(13,12,7,0.1),_1px_-2px_16px_0px_rgba(13,12,7,0.1)]
            "
          >
            <div className="w-full max-w-5xl gap-4 flex items-center justify-between">
              <button
                className={`
                      relative
                      inline-flex justify-center items-center gap-1
                      h-[48px] px-[24px] py-[14px]
                      rounded-[32px]
                      border-[1px] border-[linear-gradient(200deg,_#0D0C07%,_#353227_100%)]
                      text-[#FFF] text-center
                      text-[14px] font-medium leading-[28px]
                      transition-colors
                      w-full
                      bg-[#0D0C07]
                      shadow-[0_2px_16px_0_rgba(0,0,0,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_2px_rgba(13,12,7,0.4)]
                      cursor-pointer bg-[linear-gradient(200deg,_#0D0C07%,_#353227_100%)] md:hover:border-[0.5px] md:hover:border-[#252218] md:hover:bg-[#252218]
                    `}
                onClick={onTryAgain}
              >
                {/*<span className="absolute left-4 top-1/2 -translate-y-1/2 text-[12px] font-semibold leading-[12px] opacity-70">
                  {triesLeft} {triesLeft === 1 ? t('premium.try') : t('premium.tries')}
                </span>*/}
                <span>{t("premium.createNewMealsButton")}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      {portalContent}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLoginSuccess={handleLoginSuccess}
        context="premium"
      />
    </>
  );
}

export default TriesLeftDrawer;
